<template>
  <div class="data-table scrollbar">
    <v-data-table
      :headers="headers"
      :items="allInstances"
      :items-per-page="-1"
      fixed-header
      density="comfortable"
    >
      <template #headers="{ columns, }">
        <tr>
          <template v-for="column in columns" :key="column.key">
            <th v-if="column.key !== 'select'" class="th2">
              <template v-if="column.key === 'state'">
                <span class="mr-2 cursor-pointer" @click="() => sortStateAsc = !sortStateAsc">
                  {{ column.title }}
                  <v-icon size="16px" :icon="getSortIcon(sortStateAsc)" />
                </span>
              </template>
              <template v-else-if="column.key === 'launch'">
                <span class="mr-2 cursor-pointer" @click="() => sortDateAsc = !sortDateAsc">
                  {{ column.title }}
                  <v-icon size="16px" :icon="getSortIcon(sortDateAsc)" />
                </span>
              </template>
              <template v-else>
                <span class="mr-2">{{ column.title }}</span>
              </template>
            </th>
            <th v-else class="th2">
              <div class="select-all checkbox">
                <v-checkbox
                  v-model="selectAllInstances"
                  :hide-details="true"
                  density="compact"
                  @input="handleSelectAllClicked"
                />
              </div>
            </th>
          </template>
        </tr>
      </template>

      <template v-for="(header, i) in headers" :key="i" #[`item.${header.key}`]="{ item }">
        <template v-if="header.key ==='launch'">
          {{ formatTime(item[header.key]) }}
        </template>
        <template v-else-if="header.key ==='task_type'">
          <div class="label-icon">
            <SVGIcon
              v-if="taskTypeMap[item[header.key]]?.icon"
              :iconName="taskTypeMap[item[header.key]].icon"
              :width="'24px'"
              :height="'24px'"
              class="data-table-icon"
            />
            <div v-if="taskTypeMap[item[header.key]]?.name">
              {{ taskTypeMap[item[header.key]].name }}
            </div>
          </div>
        </template>
        <template v-else-if="header.key ==='docker_task'">
          <UserIcon
            v-if="item[header.key].username"
            :user="userList.find((e) => e.username === item[header.key].username)"
            :size="'32px'"
          />
        </template>
        <template v-else-if="header.key ==='select'">
          <div class="select-all checkbox">
            <v-checkbox
              v-model="selectedInstances"
              :hide-details="true"
              density="compact"
              :value="item"
            />
          </div>
        </template>
        <template v-else-if="header.key ==='state'">
          <span v-if="item[header.key] === 'pending'" class="status-tag _pending">Pending</span>
          <span v-else-if="item[header.key] === 'running'" class="status-tag _running">Running</span>
          <span v-else-if="item[header.key] === 'stopping'" class="status-tag _warning">Stopping</span>
          <span v-else-if="item[header.key] === 'terminated'" class="status-tag">Terminated</span>
        </template>
        <template v-else>
          {{ item[header.key] }}
        </template>
      </template>
      <template #bottom />
    </v-data-table>
  </div>
</template>

<script setup>
import {
  ref, onBeforeMount, watch, computed,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import { useStore } from 'vuex';
import UserIcon from '@/components/UserIcon.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

const store = useStore();

const emit = defineEmits([
  'update:total-instance-count',
  'update:selected-instances',
]);

const dataConnect = new DatastoreConnect();
const headers = ref([
  {
    title: 'selection', key: 'select', width: '1%', align: 'start',
  },
  {
    title: 'Name', key: 'provided_name', width: '34%', align: 'start',
  },
  {
    title: 'User', key: 'docker_task', width: '5%', align: 'center',
  },
  {
    title: 'Task', key: 'task_type', width: '20%', align: 'start',
  },
  {
    title: 'IP Address', key: 'ip', width: '15%', align: 'start',
  },
  {
    title: 'Start Date', key: 'launch', width: '15%', align: 'start',
  },
  {
    title: 'Status', key: 'state', width: '10%', align: 'start',
  },
]);
const allInstances = ref([]);
const loading = ref(false);
const selectedInstances = ref([]);
const selectAllInstances = ref(false);
const sortDateAsc = ref(false);
const sortStateAsc = ref(false);

const userList = computed(() => store.state.user.userList);

const taskTypeMap = ref({
  "aigt": {
    name: "AI Assisted Ground Truth",
    icon: "sam",
  },
  "auto-ann": {
    name: "Model Inference",
    icon: "auto_annotate",
  },
  "trainer": {
    name: "Model Training",
    icon: "science",
  },
  "validation": {
    name: "Validation",
    icon: "compare",
  },
  "snapshot-server": {
    name: "Snapshot",
    icon: "database_sync",
  },
  "snapshot-create": {
    name: "Snapshot",
    icon: "database_sync",
  },
  "snapshot-restore": {
    name: "Snapshot",
    icon: "database_sync",
  },
});

async function getInstances() {
  loading.value = true;
  selectedInstances.value = [];
  await dataConnect.listCloudInstances({ filter: "" })
    .then((data) => {
      if (data.result && !data.error) {
        emit('update:total-instance-count', data.result.length);
        allInstances.value = data.result;
      } else if (data.error) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Error listing instances: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      console.log(error);
      return '';
    });
  loading.value = false;
}

function formatTime(str) {
  return `${new Date(str).toLocaleTimeString('default', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true,
  })}`;
}

function getSortIcon(sortedAsc) {
  return sortedAsc ? 'mdi-arrow-up' : 'mdi-arrow-down';
}

watch(sortDateAsc, () => {
  sortByDate(sortDateAsc.value);
}, { deep: true });

watch(sortStateAsc, () => {
  sortByState(sortStateAsc.value);
}, { deep: true });

watch(selectedInstances, (newInstances) => {
  if (selectedInstances.value.length > 0 && selectedInstances.value.length === allInstances.value.length) {
    selectAllInstances.value = true;
  } else {
    selectAllInstances.value = false;
  }
  emit('update:selected-instances', newInstances);
}, { deep: true });

watch(selectedInstances, (newInstances) => {
  if (selectedInstances.value.length > 0 && selectedInstances.value.length === allInstances.value.length) {
    selectAllInstances.value = true;
  } else {
    selectAllInstances.value = false;
  }
  emit('update:selected-instances', newInstances);
}, { deep: true });

function sortByDate(val) {
  if (val) {
    allInstances.value.sort((a, b) => new Date(a.launch) - new Date(b.launch));
  } else {
    allInstances.value.sort((a, b) => new Date(b.launch) - new Date(a.launch));
  }
}

function sortByState(val) {
  if (val) {
    allInstances.value.sort((a, b) => a.state.localeCompare(b.state));
  } else {
    allInstances.value.sort((a, b) => b.state.localeCompare(a.state));
  }
}

function handleSelectAllClicked() {
  if (selectAllInstances.value) {
    selectedInstances.value = allInstances.value;
  } else {
    selectedInstances.value = [];
  }
}

onBeforeMount(() => {
  getInstances();
});

defineExpose({
  getInstances,
});

</script>

<style lang="scss" scoped>

.data-table {
    width: calc(100% - 40px);
    height: 100%;
    padding: 0 15px 15px 15px;
    border-radius: 8px;
    margin: 0 20px 20px 20px;
    overflow-y: auto;

  th + th:not(:nth-last-of-type(-n)) {
    border-left: 2px solid rgb(74, 76, 75);
    border-image: linear-gradient(to bottom, rgba(108,219,141,0) 25%,rgba(74, 76, 75,1) 25%,rgba(74, 76, 75,1) 75%,rgba(108,219,141,0) 75%);
    border-image-slice: 1;
  }
}

:deep(.v-table) {
  background: transparent;
  border-radius: 0;
}

.th2 {
  background: transparent !important;
  span {
    font-size: 1.0rem;
    font-weight: 600;
  }
}

.select-all {
  display: flex;
  flex-direction: row;
  width: 15px;
}

.label-icon {
  display: flex;
  flex-direction: row;
  align-items: center;
  color: var(--color-black) !important;

  :nth-child(2) {
    margin-left: 5px;
  }
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 600;
  white-space: nowrap;
  color: var(--gray-600);

  &._running {
    @include themify() {
      color: themed('color-status-complete');
    }
  }
  &._pending {
    @include themify() {
      color: themed('color-status-running');
    }
  }
  &._warning {
    @include themify() {
      color: themed('color-status-warning');
    }
  }

  div {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }
}

.cursor-pointer {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}
</style>
